// Location utilities for the landing page
//
// Strategy:
// 1) Prefer browser GPS (navigator.geolocation)
// 2) Reverse-geocode coordinates to a city-level label (BigDataCloud reverse-geocode-client)
// 3) If GPS is denied/unavailable, fall back to IP-based city (ipwho.is)
//
// Both services are keyless for basic usage and generally provide good city-level results.

const abortableFetchJson = async (url, { timeoutMs = 8000, signal, headers } = {}) => {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const res = await fetch(url, {
      signal: signal ?? controller.signal,
      headers: {
        'Accept': 'application/json',
        ...(headers || {})
      }
    });

    if (!res.ok) {
      const text = await res.text().catch(() => '');
      throw new Error(`Request failed: ${res.status} ${res.statusText}${text ? ` - ${text}` : ''}`);
    }

    return await res.json();
  } finally {
    clearTimeout(timeout);
  }
};

export const reverseGeocodeToCity = async ({ lat, lon, language }) => {
  // Docs: https://www.bigdatacloud.com/docs/api/reverse-geocode-client
  // Example: https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=...&longitude=...&localityLanguage=en
  const params = new URLSearchParams({
    latitude: String(lat),
    longitude: String(lon),
    localityLanguage: (language || 'en').split('-')[0]
  });

  const data = await abortableFetchJson(
    `https://api.bigdatacloud.net/data/reverse-geocode-client?${params.toString()}`
  );

  const city = data?.city || data?.locality || data?.principalSubdivision;
  const state = data?.principalSubdivision;
  const country = data?.countryName;
  const postcode = data?.postcode || '';

  const label = [city, state, country].filter(Boolean).join(', ');

  return {
    name: city || state || 'Current Location',
    address: label || 'Current Location',
    postalCode: postcode,
    raw: data
  };
};

export const getBrowserCoordinates = () =>
  new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('Geolocation not supported'));
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        resolve({
          lat: pos.coords.latitude,
          lon: pos.coords.longitude,
          accuracy: pos.coords.accuracy
        });
      },
      (err) => reject(err),
      {
        enableHighAccuracy: true,
        timeout: 8000,
        maximumAge: 60_000
      }
    );
  });

export const fetchIpCity = async () => {
  // ipwho.is is keyless for basic usage
  const data = await abortableFetchJson('https://ipwho.is/', { timeoutMs: 8000 });

  if (data?.success === false) {
    throw new Error(data?.message || 'ipwho.is lookup failed');
  }

  const city = data?.city;
  const region = data?.region;
  const country = data?.country;
  const postal = data?.postal;
  const lat = data?.latitude;
  const lon = data?.longitude;

  return {
    name: city || region || 'Current Location',
    address: [city, region, country].filter(Boolean).join(', ') || 'Current Location',
    postalCode: postal || '',
    lat: typeof lat === 'number' ? lat : Number(lat) || 0,
    lon: typeof lon === 'number' ? lon : Number(lon) || 0,
    raw: data
  };
};

export const detectCityLocation = async ({ language } = {}) => {
  // Try GPS first
  try {
    const coords = await getBrowserCoordinates();
    const details = await reverseGeocodeToCity({ ...coords, language });
    return {
      ...details,
      lat: coords.lat,
      lon: coords.lon,
      source: 'gps'
    };
  } catch (e) {
    // Fall back to IP-based location
    const ip = await fetchIpCity();
    return {
      name: ip.name,
      address: ip.address,
      postalCode: ip.postalCode,
      lat: ip.lat,
      lon: ip.lon,
      source: 'ip'
    };
  }
};
